import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AccountantLayout from '../components/AccountantLayout';

const SettingsPage = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({
    companyName: 'Accountant Firm Ltd',
    email: 'info@accountantfirm.com',
    phone: '+32 2 123 4567',
    address: 'Avenue Louise 123, 1050 Brussels, Belgium',
    vatNumber: 'BE0987654321',
    emailNotifications: true,
    taskReminders: true,
    clientReports: false,
    autoBackup: true,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setSettings((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate API call
    setTimeout(() => {
      toast.success(t('settings_updated_successfully'));
    }, 500);
  };

  return (
    <AccountantLayout>
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {t('accountant_settings')}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {t('manage_account_preferences')}
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Paper sx={{ p: 4, mb: 3, borderRadius: '12px' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
              {t('company_information')}
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label={t('company_name')}
                  name="companyName"
                  value={settings.companyName}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label={t('email')}
                  name="email"
                  type="email"
                  value={settings.email}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('phone')}
                  name="phone"
                  value={settings.phone}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('vat_number')}
                  name="vatNumber"
                  value={settings.vatNumber}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('address')}
                  name="address"
                  value={settings.address}
                  onChange={handleChange}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 4, mb: 3, borderRadius: '12px' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
              {t('notification_preferences')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.emailNotifications}
                      onChange={handleSwitchChange}
                      name="emailNotifications"
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">{t('email_notifications')}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('receive_email_updates')}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
              <Divider sx={{ width: '100%', my: 1 }} />
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.taskReminders}
                      onChange={handleSwitchChange}
                      name="taskReminders"
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">{t('task_reminders')}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('get_reminded_tasks')}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
              <Divider sx={{ width: '100%', my: 1 }} />
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.clientReports}
                      onChange={handleSwitchChange}
                      name="clientReports"
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">{t('weekly_client_reports')}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('receive_weekly_summaries')}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
              <Divider sx={{ width: '100%', my: 1 }} />
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.autoBackup}
                      onChange={handleSwitchChange}
                      name="autoBackup"
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">{t('automatic_backup')}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('daily_data_backup')}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            </Grid>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                px: 4,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #653a8b 100%)',
                },
              }}
            >
              {t('save_settings')}
            </Button>
          </Box>
        </form>
      </Box>
    </AccountantLayout>
  );
};

export default SettingsPage;
